import { createSelector } from 'reselect'
import Assignment from 'inbox/assignment'
import {
  selectAgentsById,
  selectAgentsByIdIncludingArchived,
} from 'selectors/agents/base'
import { selectGroupsById } from 'selectors/app'
import { selectCurrentUser } from 'ducks/currentUser/selectors'
import { emptyObj } from 'util/objects'
import { selectCurrentTicket, selectCurrentTicketUndecorated } from './current'
import { makeSelectTicketById } from './byId'

const selectCurrentTicketAssignee = createSelector(
  selectCurrentTicketUndecorated,
  ticket => (ticket && ticket.assignee) || emptyObj
)

const selectCurrentTicketAssigneeId = createSelector(
  selectCurrentTicketAssignee,
  assignee => assignee.id
)

export const selectCurrentTicketAssignedGroupId = createSelector(
  selectCurrentTicketUndecorated,
  ticket => ticket && ticket.assigned_group_id
)

const selectCurrentTicketAssignedAgent = createSelector(
  selectCurrentTicketAssigneeId,
  selectAgentsById,
  (assigneeId, agentsById) => agentsById[assigneeId]
)

const selectCurrentTicketAssignedGroup = createSelector(
  selectCurrentTicketAssignedGroupId,
  selectGroupsById,
  (groupId, groupsById) => (groupId ? groupsById[groupId] : undefined)
)

// This style of selector avoids a huge decorated ticket object, and encourages
// components to use more specific selectors. This method improves our ability
// to find performance/rerendering issues; individual selectors misbehaving are
// easier to debug than a huge ticket decorator.
export const selectCurrentTicketAssignment = createSelector(
  selectCurrentTicketAssignedAgent,
  selectCurrentTicketAssignedGroup,
  selectAgentsById,
  selectCurrentUser,
  (agent, group, agentsById, currentUser) => {
    return Assignment.build(agent, group, currentUser, agentsById)
  }
)

export const selectAssignedAgent = createSelector(
  selectCurrentTicket,
  selectAgentsByIdIncludingArchived,
  (ticket, agents) => {
    if (!ticket) return null
    if (!ticket.assignee) return null
    return agents[ticket.assignee.id]
  }
)

export const selectAssignedGroup = createSelector(
  selectCurrentTicket,
  selectGroupsById,
  (ticket, groups) => {
    if (ticket === null) return null
    return groups[ticket.assigned_group_id] || null
  }
)

export function makeSelectTicketAssignee(selectTicket) {
  return createSelector(selectTicket, ticket => ticket.assignee || emptyObj)
}

export function makeSelectTicketAssigneeId(selectTicket) {
  return createSelector(
    makeSelectTicketAssignee(selectTicket),
    assignee => assignee.id
  )
}

function makeSelectCurrentTicketAssignedAgent(selectTicket) {
  return createSelector(
    makeSelectTicketAssigneeId(selectTicket),
    selectAgentsById,
    (assigneeId, agentsById) => agentsById[assigneeId]
  )
}

export function makeSelectTicketAssignedGroupId() {
  const selectTicket = makeSelectTicketById()
  return createSelector(selectTicket, ticket => ticket.assigned_group_id)
}

export function makeSelectTicketAssignment() {
  const selectTicket = makeSelectTicketById()
  return createSelector(
    makeSelectCurrentTicketAssignedAgent(selectTicket),
    makeSelectTicketAssignedGroupId(selectTicket),
    selectAgentsById,
    selectCurrentUser,
    (agent, group, agentsById, currentUser) => {
      return Assignment.build(agent, group, currentUser, agentsById)
    }
  )
}
